import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField as MuiTextField,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as DiagramIcon,
  Factory as PlantIcon,
  Visibility as ShowIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import CustomCreateButton from '@/components/CustomCreateButton';
import PaginationControls from '@/components/PaginationControls';
import DeleteWithConfirmationDialog from '@/components/DeleteWithConfirmationDialog';
import TableComponent, { Column } from '@/components/TableComponent';
import { 
  getGetApiDiagramsQueryKey, 
  getApiDiagrams,
  deleteApiDiagramsId
} from '@/api/gen/api';

// DebouncedSearchInput (mismo componente del documento de referencia)
const DebouncedSearchInput = React.memo(({ 
  onSearchChange, 
  initialValue = '' 
}: { 
  onSearchChange: (value: string) => void;
  initialValue?: string;
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const debounceRef = React.useRef<NodeJS.Timeout>();

  const handleChange = (value: string) => {
    setInputValue(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 500);
  };

  React.useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  return (
    <MuiTextField
      size="small"
      placeholder="Buscar diagramas..."
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 200, mb: 0.5 }}
    />
  );
});

const pageSizeOptions = [5, 10, 20, 50];

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': { margin: 0 },
  '& svg': { fontSize: '16px' }
};

export const DiagramsList: React.FC = () => {
  // Estados para filtros y paginación
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  
  // Estado para el diálogo de eliminación
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    record: null as any,
  });

  const queryClient = useQueryClient();

  // Mutation para eliminar diagrama
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteApiDiagramsId(id),
    onSuccess: () => {
      // Invalidar la query para refrescar los datos
      queryClient.invalidateQueries({ queryKey: getGetApiDiagramsQueryKey({}) });
      handleCloseDeleteDialog();
    },
    onError: (error) => {
      console.error('Error eliminando diagrama:', error);
    }
  });

  // Construir parámetros para la query de diagramas
  const queryParams = useMemo(() => {
    const params: any = {
      page: pagination.page,
      pageSize: pagination.perPage,
      isPaginated: true
    };

    if (searchQuery.trim()) params.SearchString = searchQuery;
    if (filterValues.plantId) params.PlantId = filterValues.plantId;

    // Parámetros de fecha 
    if (filterValues.createdStart) params.CreatedStart = filterValues.createdStart;
    if (filterValues.createdEnd) params.CreatedEnd = filterValues.createdEnd;
    if (filterValues.updatedStart) params.UpdatedStart = filterValues.updatedStart;
    if (filterValues.updatedEnd) params.UpdatedEnd = filterValues.updatedEnd;

    return params;
  }, [filterValues, pagination, searchQuery]);

  // Query para diagramas
  const queryKey = getGetApiDiagramsQueryKey(queryParams);

  const {
    data: diagramsResponse,
    isLoading: isLoadingDiagrams,
    error: diagramsError,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => getApiDiagrams(queryParams),
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Extraer datos de diagramas
  const { diagrams, totalCount, showPagination } = useMemo(() => {
    if (!diagramsResponse) return { diagrams: [], totalCount: 0, showPagination: false };

    let items = [];
    let total = 0;

    if (Array.isArray(diagramsResponse)) {
      items = diagramsResponse;
      total = diagramsResponse.length;
    } else if (diagramsResponse.items && Array.isArray(diagramsResponse.items)) {
      items = diagramsResponse.items;
      total = diagramsResponse.totalCount || diagramsResponse.items.length;
    } else if (diagramsResponse.data && Array.isArray(diagramsResponse.data)) {
      items = diagramsResponse.data;
      total = diagramsResponse.totalCount || diagramsResponse.data.length;
    } else {
      items = [diagramsResponse];
      total = 1;
    }
    
    return {
      diagrams: items,
      totalCount: total,
      showPagination: total > pagination.perPage
    };
  }, [diagramsResponse, pagination.perPage]);

  // Handlers para eliminación
  const handleOpenDeleteDialog = (record: any) => {
    setDeleteDialog({
      open: true,
      record,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      record: null,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.record) {
      deleteMutation.mutate(deleteDialog.record.id);
    }
  };

  // Handlers
  const handleFilterChange = React.useCallback((selections: HierarchicalFilterSelections) => {
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination({ perPage, page: 1 });
  };

  // Columnas para la tabla
  const diagramColumns: Column[] = [
    {
      key: 'id',
      label: 'ID',
      width: '50px',
    },
    {
      key: 'name',
      label: 'Nombre del Diagrama',
      maxWidth: '200px',
    },
    {
      key: 'description',
      label: 'Descripción',
      maxWidth: '250px',
      render: (value) => value || 'Sin descripción',
    },
    {
      key: 'plantId',
      label: 'Planta',
      maxWidth: '150px',
      render: (value, record) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlantIcon color='action' fontSize='small' />
          <span>
            {record.plantName || `Planta ${value}` || 'Sin planta'}
          </span>
        </Box>
      ),
    },
    {
      key: 'createdAt',
      label: 'Fecha de Creación',
      maxWidth: '150px',
      render: (value) => new Date(value).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
    {
      key: 'updatedAt',
      label: 'Última Actualización',
      maxWidth: '150px',
      render: (value) => new Date(value).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
    {
      key: 'actions',
      label: 'Acciones',
      width: '120px',
      align: 'center',
      render: (value, record) => (
        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <IconButton
            size="small"
            component={Link}
            to={`/diagrams/${record.id}/show`}
            sx={buttonStyle}
            color="primary"
          >
            <ShowIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            component={Link}
            to={`/diagrams/${record.id}`}
            sx={buttonStyle}
            color="primary"  
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(record)}
            sx={buttonStyle}
            color="error"
            disabled={deleteMutation.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Card elevation={1} sx={{ m: 2, p: 2 }}>
        <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography
            variant='h5'
            component='h1'
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <DiagramIcon color='primary' />
            Gestión de Diagramas
            {isFetching && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Gestiona los diagramas del sistema organizados por plantas
          </Typography>
        </Box>

        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6' gutterBottom color='primary'>
            Filtros de Búsqueda
          </Typography>

          <HierarchicalFilter
            levels={['plant']}
            onChange={handleFilterChange}
          />

          {/* Contenedor para buscador y botón en la misma línea */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2 }}>
            <Box>
              <DebouncedSearchInput 
                onSearchChange={handleSearchChange}
                initialValue={searchQuery}
              />
            </Box>
            
            {/* Botón de crear al mismo nivel que el buscador, pegado a la derecha */}
            <CustomCreateButton
              variant='contained'
              label='Crear Diagrama'
              icon={<AddIcon />}
              sx={{
                height: '40px',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            />
          </Box>

          {filterValues.plantId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Selección actual:
                {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Título de la sección de resultados */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant='h6'>Diagramas ({totalCount})</Typography>
          </Box>

          {diagramsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error cargando diagramas: {diagramsError.message}
            </Alert>
          )}

          {isLoadingDiagrams && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoadingDiagrams && diagrams.length === 0 && (
            <Alert severity="info">No se encontraron diagramas con los filtros actuales</Alert>
          )}

          {diagrams.length > 0 && (
            <>
              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={true}
                />
              )}
              
              <TableComponent
                data={diagrams}
                columns={diagramColumns}
                loading={isLoadingDiagrams}
                emptyMessage="No se encontraron diagramas"
              />

              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={false}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      {/* Diálogo de confirmación de eliminación */}
      <DeleteWithConfirmationDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirm}
        record={deleteDialog.record}
        resource="diagrams"
        nameField="name"
        displayName={deleteDialog.record?.name}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default DiagramsList;